import { Check, Send, UserCheck, Calendar, CreditCard } from "lucide-react";

/**
 * 4-step progress tracker:
 *   Invitation Sent → Account Activated → First Booking → First Payment
 * customer: full user document. We derive each step's completion from fields
 * already present on the user doc + adjacent counts passed in via `stats`.
 *
 * Props:
 *   customer: { invitation_status, invitation_send_count, password_hash, ... }
 *   bookingCount: number of bookings created for this customer
 *   firstPaid: boolean — has the customer ever paid
 */
export default function InvitationProgressTracker({ customer, bookingCount = 0, firstPaid = false }) {
  if (!customer) return null;
  const steps = [
    {
      key: "invited",
      label: "Invitation Sent",
      icon: Send,
      done: (customer.invitation_send_count || 0) > 0 || customer.invitation_status === "invitation_sent" || customer.invitation_status === "active_account",
    },
    {
      key: "activated",
      label: "Account Activated",
      icon: UserCheck,
      done: !!customer.password_hash || customer.invitation_status === "active_account",
    },
    {
      key: "booked",
      label: "First Booking",
      icon: Calendar,
      done: bookingCount > 0,
    },
    {
      key: "paid",
      label: "First Payment",
      icon: CreditCard,
      done: firstPaid,
    },
  ];
  return (
    <div className="rounded-2xl bg-gray-50 border border-gray-200 px-3 py-3" data-testid={`progress-tracker-${customer.user_id}`}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Conversion progress</div>
      <div className="mt-2 flex items-center gap-1.5 sm:gap-2">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border-2 ${s.done ? "bg-[#047857] border-[#047857] text-white" : "bg-white border-gray-300 text-gray-400"}`}
                title={s.label}
                data-testid={`progress-${customer.user_id}-${s.key}`}
              >
                {s.done ? <Check className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5" />}
              </div>
              <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-gray-700 truncate hidden sm:block">{s.label}</div>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${steps[i + 1].done && s.done ? "bg-[#047857]" : "bg-gray-200"}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
